import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import api from "../lib/api.ts";
import { useToast } from "../context/ToastContext";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const ReviewSubmissionPopup = ({ isOpen, onClose, onSuccess }) => {
  const { showSuccess, showError } = useToast();
  const { language, translate } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    testimonial: "",
    rating: 5,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Auto-populate name field with username when popup opens and user is authenticated
  useEffect(() => {
    if (isOpen && isAuthenticated && user?.name) {
      setFormData((prev) => ({
        ...prev,
        name: user.name,
      }));
    }
  }, [isOpen, isAuthenticated, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));
    if (errors.rating) {
      setErrors((prev) => ({
        ...prev,
        rating: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name =
        translate("testimonials.validation.nameRequired") || "Name is required";
    }

    if (!formData.designation.trim()) {
      newErrors.designation =
        translate("testimonials.validation.designationRequired") ||
        "Designation is required";
    }

    if (!formData.testimonial.trim()) {
      newErrors.testimonial =
        translate("testimonials.validation.testimonialRequired") ||
        "Testimonial is required";
    } else if (formData.testimonial.trim().length < 10) {
      newErrors.testimonial =
        translate("testimonials.validation.testimonialMinLength") ||
        "Testimonial must be at least 10 characters";
    }

    if (formData.rating < 1 || formData.rating > 5) {
      newErrors.rating =
        translate("testimonials.validation.ratingRequired") ||
        "Please select a rating";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user is logged in
    if (!isAuthenticated) {
      showError(
        translate("testimonials.messages.loginRequired") ||
          "Please login to submit a review"
      );
      return;
    }

    if (!validateForm()) {
      showError(
        translate("testimonials.validation.fixErrorsBeforeSubmitting") ||
          "Please fix the errors before submitting"
      );
      return;
    }

    try {
      setIsSubmitting(true);

      const reviewData = {
        name: formData.name.trim(),
        designation: formData.designation.trim(),
        testimonial: formData.testimonial.trim(),
        rating: formData.rating,
      };

      const response = await api.post("/reviews", reviewData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(
          translate("testimonials.messages.reviewSubmittedSuccessfully") ||
            "Thank you! Your review has been submitted."
        );
        // Reset form but keep name if user is authenticated
        setFormData({
          name: isAuthenticated && user?.name ? user.name : "",
          designation: "",
          testimonial: "",
          rating: 5,
        });
        setErrors({});
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      const errorMessage =
        error.response?.data?.message ||
        translate("testimonials.messages.failedToSubmitReview") ||
        "Failed to submit review. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      designation: "",
      testimonial: "",
      rating: 5,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='flex justify-between items-center p-4 sm:p-6 border-b border-gray-200'>
          <h2 className='text-lg sm:text-xl font-bold text-gray-800'>
            {translate("testimonials.submitReview") || "Submit a Review"}
          </h2>
          <button
            onClick={handleClose}
            className='text-gray-400 hover:text-gray-600 transition-colors'
            aria-label='Close'
          >
            <X className='w-5 h-5 sm:w-6 sm:h-6' />
          </button>
        </div>

        {/* Login Required Message */}
        {!isAuthenticated && (
          <div className='p-4 sm:p-6 border-b border-gray-200 bg-yellow-50'>
            <p className='text-sm text-yellow-800'>
              {translate("testimonials.messages.loginRequired") ||
                "Please login to submit a review"}
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className='p-4 sm:p-6 space-y-4'>
          {/* Name */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {translate("testimonials.name") || "Name"}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              placeholder={
                translate("testimonials.namePlaceholder") || "Enter your name"
              }
              className={`w-full border rounded-lg p-2 sm:p-3 text-gray-600 placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.name && (
              <p className='text-red-500 text-xs mt-1'>{errors.name}</p>
            )}
          </div>

          {/* Designation */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {translate("testimonials.designation") || "Designation"}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              name='designation'
              value={formData.designation}
              onChange={handleInputChange}
              placeholder={
                translate("testimonials.designationPlaceholder") ||
                "Enter your designation"
              }
              className={`w-full border rounded-lg p-2 sm:p-3 text-gray-600 placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                errors.designation ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.designation && (
              <p className='text-red-500 text-xs mt-1'>{errors.designation}</p>
            )}
          </div>

          {/* Rating */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              {translate("testimonials.rating") || "Rating"}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <div className='flex items-center gap-2'>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type='button'
                  onClick={() => handleRatingChange(star)}
                  className={`text-2xl sm:text-3xl transition-colors ${
                    star <= formData.rating ? "text-[#e95923]" : "text-gray-300"
                  } hover:text-[#e95923]`}
                  aria-label={`Rate ${star} stars`}
                >
                  ★
                </button>
              ))}
              <span className='text-sm text-gray-600 ml-2'>
                ({formData.rating}/5)
              </span>
            </div>
            {errors.rating && (
              <p className='text-red-500 text-xs mt-1'>{errors.rating}</p>
            )}
          </div>

          {/* Testimonial */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {translate("testimonials.testimonial") || "Testimonial"}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <textarea
              name='testimonial'
              value={formData.testimonial}
              onChange={handleInputChange}
              placeholder={
                translate("testimonials.testimonialPlaceholder") ||
                "Write your testimonial here..."
              }
              rows={4}
              className={`w-full border rounded-lg p-2 sm:p-3 text-gray-600 placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none ${
                errors.testimonial ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.testimonial && (
              <p className='text-red-500 text-xs mt-1'>{errors.testimonial}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className='flex gap-3 pt-2'>
            <button
              type='button'
              onClick={handleClose}
              disabled={isSubmitting}
              className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50'
            >
              {translate("testimonials.cancel") || "Cancel"}
            </button>
            <button
              type='submit'
              disabled={isSubmitting || !isAuthenticated}
              className='flex-1 px-4 py-2 bg-[#e95923] text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isSubmitting
                ? translate("testimonials.submitting") || "Submitting..."
                : translate("testimonials.submit") || "Submit Review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewSubmissionPopup;
